import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import { Condition, ConditionAddress, ConditionGroup, MediaElement, MediaText } from 'editor/src/store/design/types';
import getVisibleColorAtElementPosition from 'editor/src/store/design/util/getVisibleColorAtElementPosition';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import fontStylesToTextElement from 'editor/src/util/fontStylesToTextElement';
import pt2mm from 'editor/src/util/pt2mm';
import { elementUuids } from 'editor/src/util/uuids';

import updateTextElementWithoutRender from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

import addElementToConditionOperation from './addElementToConditionOperation';
import addMediaElementOperation from './addMediaElementOperation';
import getNewElementDisplay from './getNewElementDisplay';
import getNewTextDisplaySize from './getNewTextDisplaySize';
import updateConditionActiveOptionOperation from './updateConditionActiveOptionOperation';

const FABRIC_SIZE_MULTIPLIER = 1.13;

function isMediaText(element: MediaElement): element is MediaText {
  return element.type === 'text';
}

// Returns the text element linked to the first option (in option order) that
// already has a linked text. Used as the style/position template for newly
// created "Add text & link" elements so every option's text stays consistent.
function getFirstLinkedOptionText(
  conditionGroup: ConditionGroup,
  condition: Condition,
  media: MediaElement[] | undefined,
): MediaText | undefined {
  let sourceText: MediaText | undefined;
  condition.options.some((option) => {
    const children = conditionGroup.children[getConditionChildrenKey(condition.id, option.id)];
    const linked = children
      ?.map((child) => media?.find((el) => el.uuid === child.id))
      .find((el): el is MediaText => !!el && isMediaText(el));
    if (linked) {
      sourceText = linked;
      return true;
    }
    return false;
  });
  return sourceText;
}

const createAndLinkTextToConditionOperation =
  (spreadIndex: number, conditionAddress: ConditionAddress): Thunk =>
  (dispatch, getState, { i18n }) => {
    const state = getState();
    const condition =
      state.design.designData?.spreads[spreadIndex]?.conditionGroup?.conditions[conditionAddress.conditionId];
    if (!condition) {
      return;
    }

    const conditionGroup = state.design.designData?.spreads[spreadIndex]?.conditionGroup;
    const freeOption = conditionGroup
      ? condition.options.find((option) => {
          const children = conditionGroup.children[getConditionChildrenKey(condition.id, option.id)];
          return !children || children.length === 0;
        })
      : undefined;

    const minFontSize = getHostSetting(state, 'minFontSize');
    const maxFontSize = getHostSetting(state, 'maxFontSize');
    const display = getNewElementDisplay(state, spreadIndex, getNewTextDisplaySize(minFontSize, maxFontSize));
    if (!display) {
      return;
    }

    const { center, rotation, width, fontSize, area } = display;
    const defaultTextStyle = state.fonts.defaultFontStyles;
    const { fontFamily } = state.editor.currentStyles.text;
    const newUuid = elementUuids.generate();

    const text = freeOption?.label || i18n.t('editor-new-text-sample');

    const textElement: MediaText = {
      type: 'text',
      group: '',
      name: `${i18n.t('editor-text')} ${newUuid}`,
      sx: 1,
      sy: 1,
      rendered_with: 'opentype.js',
      x: center.x,
      y: center.y,
      width,
      height: pt2mm(fontSize) * FABRIC_SIZE_MULTIPLIER,
      uuid: newUuid,
      personalizationLocked: undefined,
      ...fontStylesToTextElement({ ...defaultTextStyle, fontFamily, fontSize }, text),
    };
    textElement.r += rotation;

    updateTextElementWithoutRender(textElement, undefined);
    const centerX = area.left + area.width / 2;
    const centerY = area.top + area.height / 2;
    textElement.x = centerX - textElement.width / 2;
    textElement.y = centerY - textElement.height / 2;

    textElement.fill = getVisibleColorAtElementPosition(textElement, spreadIndex, state);

    // Inherit style + position from the first option's existing text so the new
    // option's text lines up and matches it (mirrors "Apply text style to all
    // options"). Text content stays the new option's label. Falls back to the
    // default style/position above when no option has a linked text yet.
    const currentMedia = state.design.designData?.spreads[spreadIndex]?.pages[0]?.groups.media;
    const sourceText = conditionGroup ? getFirstLinkedOptionText(conditionGroup, condition, currentMedia) : undefined;
    if (sourceText) {
      textElement.x = sourceText.x;
      textElement.y = sourceText.y;
      textElement.width = sourceText.width;
      textElement.height = sourceText.height;
      textElement.r = sourceText.r;
      textElement.sx = sourceText.sx;
      textElement.sy = sourceText.sy;
      textElement.fill = sourceText.fill;
      textElement.extra = {
        ...textElement.extra,
        fontSize: sourceText.extra.fontSize,
        fontFamily: sourceText.extra.fontFamily,
        textAlign: sourceText.extra.textAlign,
        lineHeight: sourceText.extra.lineHeight,
        underline: sourceText.extra.underline,
        linethrough: sourceText.extra.linethrough,
        letterSpacing: sourceText.extra.letterSpacing,
        stroke: sourceText.extra.stroke,
        curve: sourceText.extra.curve,
        shadow: sourceText.extra.shadow,
        direction: sourceText.extra.direction,
        flexibleWidth: sourceText.extra.flexibleWidth,
        wrapping: sourceText.extra.wrapping,
      };
    }

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Create and link text element'));
      dispatch(addMediaElementOperation(spreadIndex, 0, textElement, false));
      if (freeOption) {
        const newState = getState();
        const media = newState.design.designData?.spreads[spreadIndex]?.pages[0]?.groups.media;
        const element = media?.find((el) => el.uuid === newUuid);
        if (element) {
          dispatch(addElementToConditionOperation(conditionAddress, freeOption.id, element));
        }
        if (freeOption.id !== condition.activeOptionId) {
          dispatch(updateConditionActiveOptionOperation(conditionAddress, freeOption.id));
        }
      }
    });
  };

export default createAndLinkTextToConditionOperation;
